import React, { useCallback, useEffect, useState } from 'react';
import { parse } from 'query-string';
import { useLocation, useParams } from 'react-router-dom';
import { useCSMParentsGetAll } from '../../../api/csm';
import { COLORS, FlexView, Row, Separator } from '@cuemath/comps';
import { Table, TableCell } from 'comps';
import Search from 'modules/list/new-search-bar';
import { TableRow } from 'semantic-ui-react';
import Pagination from 'modules/list/pagination';
import styled, { keyframes } from 'styled-components';
import { Text } from '@cuemath/web-comps';
import { getUserFullName } from 'utils';

const StudentWrapper = styled(FlexView)`
  padding: 8px 12px;
  margin: 0 -12px;
  border-bottom: 1px dashed ${COLORS.GHOST};
  :first-child {
    padding-top: 4px;
  }
  :last-child {
    padding-bottom: 4px;
    border-bottom: none;
  }
`;

const StudentName = styled(Text)`
  cursor: pointer;
  text-transform: capitalize;
  &:hover {
    text-decoration: underline;
  }
`;

const FadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const Container = styled(FlexView)`
  padding: 0 16px;
  animation: ${FadeIn} 0.5s ease-in-out;
`;

const PaginationWrapper = styled(Row)`
  height: 42px;
`;

const TableWrapper = styled(Row)`
  margin-top: 24px;
  width: 100%;
`;

const CsmAssignedParents = () => {
  const location = useLocation();
  const { search } = location;
  const { csmId } = useParams();
  const { activeTab } = parse(search);
  const [page, setPage] = useState(1);
  const [searchVal, setSearchVal] = useState('');

  const {
    data: csmParents,
    getAll: csmParentsGetAll,
    isProcessing: isParentsLoading,
  } = useCSMParentsGetAll();

  const fetchList = useCallback(() => {
    const query = {
      csmId,
      page,
      search: searchVal,
      per_page: 10,
    };
    if (!searchVal) delete query.search;

    csmParentsGetAll(query);
  }, [csmId, page, searchVal, activeTab]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handlePageChange = useCallback(newPage => {
    setPage(newPage);
  }, []);

  const handleSearch = useCallback(val => {
    setPage(1);
    setSearchVal(val);
  }, []);

  const showPagination = csmParents?.length > 0;

  return (
    <Container>
      <Row justifyContent="flex-end">
        <Search
          showNewSearchBar={true}
          onSearch={handleSearch}
          placeholder="Search parents by ID"
        />
      </Row>
      <TableWrapper>
        <Table
          headerCell={[{ title: 'Name' }, { title: 'Students' }]}
          dataLength={csmParents?.length || 0}
          havePagination
          showInfo={false}
          showLoader={isParentsLoading}
          flexBasis="100%"
          scrollBodyHeight={isParentsLoading || !showPagination ? '532px' : '100%'}
        >
          {(csmParents ?? [])?.map(({ id, name, students }) => (
            <TableRow key={id}>
              <TableCell width={4}>{name}</TableCell>
              <TableCell width={4}>
                <FlexView>
                  {students?.map(
                    ({
                      intel_enrollment_id: id,
                      first_name = '',
                      last_name = '',
                      grade,
                    }) => (
                      <StudentWrapper
                        alignItems="center"
                        flexDirection="row"
                        onClick={() => {
                          id && window.open(`/students/${id}`, '_blank');
                        }}
                        key={id}
                      >
                        <StudentName variant="body2">
                          {getUserFullName(first_name, last_name)}&nbsp;
                        </StudentName>
                        <Text variant="body3" color={COLORS.BLACK_75}>
                          • &nbsp;Grade {grade}
                        </Text>
                      </StudentWrapper>
                    ),
                  )}
                </FlexView>
              </TableCell>
            </TableRow>
          ))}
        </Table>
      </TableWrapper>
      <Separator transparent height={8} />
      <PaginationWrapper justifyContent="center" alignItems="center">
        {showPagination && (
          <Pagination
            page={page}
            perPage={10}
            count={null}
            onPageChange={handlePageChange}
            shouldShowNewUi
          />
        )}
      </PaginationWrapper>
    </Container>
  );
};

export default CsmAssignedParents;
